import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * FASE J — Wizard Viaggi (Tempo Libero).
 * Step 0: Destinazione + durata
 * Step 1: Viaggiatori (ArrayField) + coperture + massimali
 */

const TIPI_VIAGGIO = [
  "Turismo / leisure",
  "Business",
  "Studio",
  "Lavoro con soggiorno lungo",
  "Missione internazionale",
];

const COPERTURE = [
  { k: "spese_mediche",       l: "Spese mediche" },
  { k: "bagaglio",             l: "Bagaglio" },
  { k: "annullamento",         l: "Annullamento viaggio" },
  { k: "rimpatrio_sanitario", l: "Rimpatrio sanitario" },
  { k: "assistenza_24h",       l: "Assistenza 24/7" },
  { k: "responsabilita_civile",l: "Responsabilità civile viaggiatore" },
];

const SPORT_RISCHIO = [
  "Sci / snowboard", "Immersioni subacquee", "Arrampicata / trekking d'alta quota",
  "Motociclismo off-road", "Parapendio / paracadutismo", "Safari / adventure sport",
];

export function ViaggiStep0({ formData, update }) {
  return (
    <div className="space-y-5" data-testid="viaggi-step-0">
      <p className="text-sm text-[#4A5E51]">
        Definisci destinazione e durata del viaggio.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm text-[#0F1F14]">Destinazione (paese/area) <span className="text-[#D97B58]">*</span></Label>
          <Input
            value={formData.destinazione || ""}
            onChange={e => update("destinazione", e.target.value)}
            className="rounded-xl border-[#E5E2DB] h-11"
            placeholder="es. Thailandia, Europa, USA"
            data-testid="viaggi-destinazione"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm text-[#0F1F14]">Tipo viaggio <span className="text-[#D97B58]">*</span></Label>
          <Select value={formData.tipo_viaggio || ""}
                  onValueChange={v => update("tipo_viaggio", v)}>
            <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid="viaggi-tipo">
              <SelectValue placeholder="Seleziona" />
            </SelectTrigger>
            <SelectContent>
              {TIPI_VIAGGIO.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label className="text-sm text-[#0F1F14]">Data partenza</Label>
          <Input type="date"
                  value={formData.data_partenza || ""}
                  onChange={e => update("data_partenza", e.target.value)}
                  className="rounded-xl border-[#E5E2DB] h-11"
                  data-testid="viaggi-data-partenza" />
        </div>
        <div className="space-y-2">
          <Label className="text-sm text-[#0F1F14]">Data rientro</Label>
          <Input type="date"
                  value={formData.data_rientro || ""}
                  onChange={e => update("data_rientro", e.target.value)}
                  className="rounded-xl border-[#E5E2DB] h-11"
                  data-testid="viaggi-data-rientro" />
        </div>
        <div className="space-y-2">
          <Label className="text-sm text-[#0F1F14]">Durata (giorni)</Label>
          <Input type="number" min={1} max={365}
                  value={formData.durata_giorni || ""}
                  onChange={e => update("durata_giorni", e.target.value)}
                  className="rounded-xl border-[#E5E2DB] h-11"
                  placeholder="14"
                  data-testid="viaggi-durata" />
        </div>
      </div>
    </div>
  );
}

export function ViaggiStep1({ formData, update, toggleArray }) {
  const viaggiatori = formData.viaggiatori || [];

  const add = () => update("viaggiatori",
    [...viaggiatori, { nome: "", cognome: "", anno_nascita: "", codice_fiscale: "" }]);
  const upd = (i, k, v) => {
    const next = [...viaggiatori];
    next[i] = { ...next[i], [k]: v };
    update("viaggiatori", next);
  };
  const rm = (i) => update("viaggiatori", viaggiatori.filter((_, x) => x !== i));

  return (
    <div className="space-y-5" data-testid="viaggi-step-1">
      {/* Viaggiatori ArrayField */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <Label className="text-sm text-[#0F1F14]">Viaggiatori coperti <span className="text-[#D97B58]">*</span></Label>
            <p className="text-xs text-[#4A5E51]">Aggiungi tutti i viaggiatori (nome, CF, anno nascita).</p>
          </div>
          <Button type="button" variant="outline" size="sm"
                    onClick={add}
                    disabled={viaggiatori.length >= 10}
                    className="rounded-full border-[#163321]/40 text-[#163321] hover:bg-[#163321]/5"
                    data-testid="viaggi-add-viaggiatore">
            <Plus className="w-4 h-4 mr-1" /> Aggiungi
          </Button>
        </div>
        {viaggiatori.length === 0 && (
          <p className="text-xs italic text-[#4A5E51]">Aggiungi almeno 1 viaggiatore.</p>
        )}
        <div className="space-y-3">
          {viaggiatori.map((v, i) => (
            <div key={i}
                  className="p-4 rounded-xl border border-[#E5E2DB] bg-[#FCFCFA] space-y-3"
                  data-testid={`viaggi-viaggiatore-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-[#4A5E51] uppercase tracking-wide">
                  Viaggiatore {i + 1}
                </span>
                <button type="button" onClick={() => rm(i)}
                          className="text-[#D97B58] hover:text-[#C06A48]"
                          data-testid={`viaggi-viaggiatore-rm-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Nome</Label>
                  <Input value={v.nome || ""} onChange={e => upd(i, "nome", e.target.value)}
                          className="h-10 rounded-xl mt-1"
                          data-testid={`viaggi-v-nome-${i}`} />
                </div>
                <div>
                  <Label className="text-xs">Cognome</Label>
                  <Input value={v.cognome || ""} onChange={e => upd(i, "cognome", e.target.value)}
                          className="h-10 rounded-xl mt-1"
                          data-testid={`viaggi-v-cognome-${i}`} />
                </div>
                <div>
                  <Label className="text-xs">Anno di nascita</Label>
                  <Input type="number" min={1920} max={2025}
                          value={v.anno_nascita || ""}
                          onChange={e => upd(i, "anno_nascita", e.target.value)}
                          className="h-10 rounded-xl mt-1"
                          data-testid={`viaggi-v-anno-${i}`} />
                </div>
                <div>
                  <Label className="text-xs">Codice Fiscale</Label>
                  <Input value={v.codice_fiscale || ""}
                          onChange={e => upd(i, "codice_fiscale", e.target.value.toUpperCase())}
                          className="h-10 rounded-xl mt-1 font-mono"
                          maxLength={16}
                          data-testid={`viaggi-v-cf-${i}`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Coperture */}
      <div className="pt-3 border-t border-[#E5E2DB]">
        <Label className="text-sm text-[#0F1F14]">Coperture richieste</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
          {COPERTURE.map(c => (
            <label key={c.k}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer text-sm transition-all ${
                      (formData.coperture || []).includes(c.k)
                        ? "border-[#163321] bg-[#163321]/5"
                        : "border-[#E5E2DB] hover:bg-[#F5F4F0]"
                    }`}
                    data-testid={`viaggi-cop-${c.k}`}>
              <Checkbox
                checked={(formData.coperture || []).includes(c.k)}
                onCheckedChange={() => toggleArray("coperture", c.k)}
                className="data-[state=checked]:bg-[#163321]"
              />
              <span className="text-[#0F1F14]">{c.l}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Massimali */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
        <div className="space-y-2">
          <Label className="text-sm text-[#0F1F14]">Massimale spese mediche (€)</Label>
          <Select
            value={formData.massimale_medico_eur ? String(formData.massimale_medico_eur) : ""}
            onValueChange={v => update("massimale_medico_eur", parseInt(v))}
          >
            <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid="viaggi-massimale-medico">
              <SelectValue placeholder="Seleziona" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30000">30.000 €</SelectItem>
              <SelectItem value="100000">100.000 €</SelectItem>
              <SelectItem value="250000">250.000 €</SelectItem>
              <SelectItem value="500000">500.000 €</SelectItem>
              <SelectItem value="1000000">1.000.000 €</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-sm text-[#0F1F14]">Massimale bagaglio (€)</Label>
          <Select
            value={formData.massimale_bagaglio_eur ? String(formData.massimale_bagaglio_eur) : ""}
            onValueChange={v => update("massimale_bagaglio_eur", parseInt(v))}
          >
            <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid="viaggi-massimale-bagaglio">
              <SelectValue placeholder="Seleziona" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Nessuna copertura bagaglio</SelectItem>
              <SelectItem value="500">500 €</SelectItem>
              <SelectItem value="1500">1.500 €</SelectItem>
              <SelectItem value="3000">3.000 €</SelectItem>
              <SelectItem value="5000">5.000 €</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* FASE G — Bundle Viaggio+Salute famiglia */}
      {(formData.viaggiatori || []).length > 0 && (
        <div className="pt-4 border-t border-[#E5E2DB]">
          <label
            className={`flex items-start gap-3 p-4 rounded-2xl border-2 cursor-pointer transition-all ${
              formData.bundle_salute_famiglia
                ? "border-[#B9A268] bg-[#F7F5EE]"
                : "border-[#E5E2DB] bg-white hover:border-[#B9A268]/40"
            }`}
            data-testid="viaggi-bundle-salute-toggle"
          >
            <Checkbox
              checked={!!formData.bundle_salute_famiglia}
              onCheckedChange={(v) => update("bundle_salute_famiglia", !!v)}
              className="data-[state=checked]:bg-[#B9A268] mt-0.5"
            />
            <div className="flex-1">
              <div className="font-heading text-sm text-[#0F1F14] mb-1">
                🎯 Vuoi assicurare anche la salute della tua famiglia in viaggio?
              </div>
              <div className="text-xs text-[#4A5E51] leading-relaxed">
                Aggiungiamo automaticamente una <b>bozza preventivo Salute</b> per i {(formData.viaggiatori || []).length} viaggiatori sopra.
                Ti contatteremo entro 24h per completare le tue esigenze sanitarie. Nessun impegno.
              </div>
            </div>
          </label>
        </div>
      )}

      {/* Sport rischio */}
      <div className="pt-2">
        <Label className="text-sm text-[#0F1F14]">Sport/attività a rischio praticati durante il viaggio</Label>
        <p className="text-xs text-[#4A5E51]">Seleziona tutti quelli che praticherai (anche occasionalmente).</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
          {SPORT_RISCHIO.map(s => (
            <label key={s}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer text-sm transition-all ${
                      (formData.sport_attivita_rischio || []).includes(s)
                        ? "border-[#163321] bg-[#163321]/5"
                        : "border-[#E5E2DB] hover:bg-[#F5F4F0]"
                    }`}
                    data-testid={`viaggi-sport-${s.replace(/[^a-z]/gi, "").toLowerCase()}`}>
              <Checkbox
                checked={(formData.sport_attivita_rischio || []).includes(s)}
                onCheckedChange={() => toggleArray("sport_attivita_rischio", s)}
                className="data-[state=checked]:bg-[#163321]"
              />
              <span className="text-[#0F1F14]">{s}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

export function renderViaggiStep(step, formData, update, toggleArray) {
  if (step === 0) return <ViaggiStep0 formData={formData} update={update} />;
  if (step === 1) return <ViaggiStep1 formData={formData} update={update} toggleArray={toggleArray} />;
  return null;
}
